// reporting-module.js
// Vuex store module for reporting functionality

const state = {
  // Report configurations
  reportConfigurations: [],
  activeReportConfig: null,

  // Report data
  reportData: null,
  isLoadingReport: false,
  reportError: null,

  // Export options
  exportFormats: ['PDF', 'CSV', 'Excel'],
  selectedExportFormat: 'PDF',

  // Filters
  dateRange: {
    startDate: null,
    endDate: null
  },
  categoryFilters: [],
  selectedCategories: [],

  // Custom reports
  savedCustomReports: [],

  // Dashboard settings
  dashboardLayout: 'grid', // 'grid' or 'list'
  favoriteReports: []
};

const getters = {
  getReportConfigurations: state => state.reportConfigurations,
  getActiveReportConfig: state => state.activeReportConfig,
  getReportData: state => state.reportData,
  isReportLoading: state => state.isLoadingReport,
  getReportError: state => state.reportError,
  getExportFormats: state => state.exportFormats,
  getSelectedExportFormat: state => state.selectedExportFormat,
  getDateRange: state => state.dateRange,
  getCategoryFilters: state => state.categoryFilters,
  getSelectedCategories: state => state.selectedCategories,
  getSavedCustomReports: state => state.savedCustomReports,
  getDashboardLayout: state => state.dashboardLayout,
  getFavoriteReports: state => state.favoriteReports
};

const actions = {
  async fetchReportConfigurations({ commit }) {
    try {
      // Import the reports API module
      const reportsApi = await import('@/services/reports');

      // In a real implementation, this would fetch configurations from an API
      // For now, we'll define some standard report configurations
      const configurations = [
        {
          id: 1,
          name: 'Monthly Expense Summary',
          type: 'expense',
          period: 'monthly',
          params: {
            groupBy: 'month'
          }
        },
        {
          id: 2,
          name: 'Transaction History',
          type: 'transaction',
          period: 'all',
          params: {
            type: ''  // All transaction types
          }
        },
        {
          id: 3,
          name: 'Expense Requests',
          type: 'request',
          period: 'quarterly',
          params: {
            status: ''  // All statuses
          }
        },
        {
          id: 4,
          name: 'Branch Performance',
          type: 'branch',
          period: 'monthly',
          params: {}
        },
        {
          id: 5,
          name: 'User Activity',
          type: 'user',
          period: 'monthly',
          params: {}
        },
        {
          id: 6,
          name: 'KPI Dashboard',
          type: 'kpi',
          period: 'monthly',
          params: {}
        }
      ];

      commit('SET_REPORT_CONFIGURATIONS', configurations);
    } catch (error) {
      console.error('Error fetching report configurations:', error);
    }
  },

  async generateReport({ commit, state }, reportConfig) {
    commit('SET_LOADING_REPORT', true);
    commit('SET_REPORT_ERROR', null);
    commit('SET_ACTIVE_REPORT_CONFIG', reportConfig);

    try {
      // Use the reports API module to fetch data based on report type
      let reportData;
      const params = {
        startDate: state.dateRange.startDate,
        endDate: state.dateRange.endDate,
        ...reportConfig.params
      };

      // Import the reports API module
      const reportsApi = await import('@/services/reports');

      // Call the appropriate API function based on report type
      switch (reportConfig.type) {
        case 'expense':
          reportData = await reportsApi.getExpensesSummary(params);
          break;
        case 'transaction':
          reportData = await reportsApi.getTransactions(params);
          break;
        case 'request':
          reportData = await reportsApi.getExpenseRequests(params);
          break;
        case 'branch':
          reportData = await reportsApi.getBranchPerformance(params);
          break;
        case 'user':
          reportData = await reportsApi.getUserActivity(params);
          break;
        case 'kpi':
          reportData = await reportsApi.getKpiDashboard(params);
          break;
        default:
          // Fallback to dashboard summary for unknown report types
          reportData = await reportsApi.getDashboardSummary(params);
      }

      // Add metadata to the report data
      reportData = {
        ...reportData,
        title: reportConfig.name,
        generatedAt: new Date().toISOString()
      };

      commit('SET_REPORT_DATA', reportData);
    } catch (error) {
      commit('SET_REPORT_ERROR', error.message || 'Failed to generate report');
      console.error('Error generating report:', error);
    } finally {
      commit('SET_LOADING_REPORT', false);
    }
  },

  setExportFormat({ commit }, format) {
    commit('SET_EXPORT_FORMAT', format);
  },

  setDateRange({ commit }, { startDate, endDate }) {
    commit('SET_DATE_RANGE', { startDate, endDate });
  },

  setCategoryFilters({ commit }, categories) {
    commit('SET_CATEGORY_FILTERS', categories);
  },

  selectCategories({ commit }, selectedCategories) {
    commit('SET_SELECTED_CATEGORIES', selectedCategories);
  },

  saveCustomReport({ commit, state }, customReport) {
    const newReport = {
      id: Date.now(),
      ...customReport,
      createdAt: new Date().toISOString()
    };
    commit('ADD_CUSTOM_REPORT', newReport);
  },

  setDashboardLayout({ commit }, layout) {
    commit('SET_DASHBOARD_LAYOUT', layout);
  },

  toggleFavoriteReport({ commit, state }, reportId) {
    const isFavorite = state.favoriteReports.includes(reportId);
    if (isFavorite) {
      commit('REMOVE_FAVORITE_REPORT', reportId);
    } else {
      commit('ADD_FAVORITE_REPORT', reportId);
    }
  },

  async exportReport({ state }) {
    try {
      // Import the reports API module
      const reportsApi = await import('@/services/reports');

      // Prepare export parameters
      const params = {
        reportType: state.activeReportConfig?.type || 'dashboard',
        format: state.selectedExportFormat.toLowerCase(),
        filters: {
          startDate: state.dateRange.startDate,
          endDate: state.dateRange.endDate,
          categories: state.selectedCategories,
          ...state.activeReportConfig?.params
        }
      };

      // Call the exportReport function from the reports API
      const result = await reportsApi.exportReport(params);

      console.log(`Exporting report in ${state.selectedExportFormat} format...`);
      return result.fileUrl;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }
};

const mutations = {
  SET_REPORT_CONFIGURATIONS(state, configurations) {
    state.reportConfigurations = configurations;
  },
  SET_ACTIVE_REPORT_CONFIG(state, config) {
    state.activeReportConfig = config;
  },
  SET_REPORT_DATA(state, data) {
    state.reportData = data;
  },
  SET_LOADING_REPORT(state, isLoading) {
    state.isLoadingReport = isLoading;
  },
  SET_REPORT_ERROR(state, error) {
    state.reportError = error;
  },
  SET_EXPORT_FORMAT(state, format) {
    state.selectedExportFormat = format;
  },
  SET_DATE_RANGE(state, { startDate, endDate }) {
    state.dateRange = { startDate, endDate };
  },
  SET_CATEGORY_FILTERS(state, categories) {
    state.categoryFilters = categories;
  },
  SET_SELECTED_CATEGORIES(state, selectedCategories) {
    state.selectedCategories = selectedCategories;
  },
  ADD_CUSTOM_REPORT(state, report) {
    state.savedCustomReports.push(report);
  },
  SET_DASHBOARD_LAYOUT(state, layout) {
    state.dashboardLayout = layout;
  },
  ADD_FAVORITE_REPORT(state, reportId) {
    state.favoriteReports.push(reportId);
  },
  REMOVE_FAVORITE_REPORT(state, reportId) {
    state.favoriteReports = state.favoriteReports.filter(id => id !== reportId);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
